// ts-rest contract for the portfolio construction framework (PCF) module
// (PRD #74, Gap Analysis Module 7).
//
// Endpoints under /api/pcf/*:
//   GET  /api/pcf/dashboard               — framework compliance dashboard
//   GET  /api/pcf/models                  — house model catalogue
//   GET  /api/pcf/models/:modelId         — single model + allocations
//   GET  /api/pcf/buy-list                — approved buy list
//   GET  /api/pcf/restricted-list         — restricted list
//   GET  /api/pcf/deviations/:id          — single deviation detail
//   POST /api/pcf/deviation/:id/justify   — IM submits rationale, emits IFC item
//   POST /api/pcf/buy-list/change         — compute affected-client impact preview
//   POST /api/pcf/buy-list/change/:id/commit — commit a previewed change
//
// The model-portfolio compliance evaluator + buy-list-change impact
// computation are pure modules in
// packages/ontology-wealth/src/pcf-evaluator.ts.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Model portfolio catalogue
// ---------------------------------------------------------------------------

export const houseModelAllocationSchema = z.object({
    assetClass: z.string(),
    targetPct: z.number(),
    lowerBandPct: z.number(),
    upperBandPct: z.number(),
});
export type HouseModelAllocation = z.infer<typeof houseModelAllocationSchema>;

export const houseModelSchema = z.object({
    id: z.string(),
    modelId: z.string(),
    name: z.string(),
    riskProfile: z.string(),
    description: z.string(),
    benchmark: z.string(),
    expectedVolatilityPct: z.number(),
    sectorCaps: z.array(z.object({ sector: z.string(), capPct: z.number() })),
    currencyExposureNote: z.string(),
    status: z.enum(['active', 'retired']),
    allocations: z.array(houseModelAllocationSchema),
});
export type HouseModel = z.infer<typeof houseModelSchema>;

// ---------------------------------------------------------------------------
// Buy / restricted list
// ---------------------------------------------------------------------------

export const securityListEntrySchema = z.object({
    id: z.string(),
    listKind: z.enum(['approved_buy', 'restricted']),
    scopeKind: z.enum(['model', 'mandate']),
    scopeId: z.string(),
    securityId: z.string(),
    securityName: z.string(),
    assetClass: z.string().nullable(),
    sector: z.string().nullable(),
    researchAttribution: z.string().nullable(),
    reason: z.string().nullable(),
    effectiveFrom: z.string(),
});
export type SecurityListEntry = z.infer<typeof securityListEntrySchema>;

// ---------------------------------------------------------------------------
// Deviation
// ---------------------------------------------------------------------------

export const pcfDeviationDetailSchema = z.object({
    id: z.string(),
    portfolioId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    imName: z.string(),
    modelId: z.string(),
    modelName: z.string(),
    ruleKind: z.enum(['asset_class_band', 'restricted_holding', 'unapproved_holding']),
    assetClass: z.string().nullable(),
    securityId: z.string().nullable(),
    securityName: z.string().nullable(),
    severity: z.enum(['minor', 'material', 'critical']),
    classification: z.enum(['deliberate_documented', 'drift_undocumented']),
    patternNote: z.string().nullable(),
    status: z.enum(['New', 'UnderReview', 'Remediating', 'Resolved']),
    raisedAt: z.string(),
    originFindingId: z.string(),
    justification: z
        .object({
            id: z.string(),
            rationale: z.string(),
            submittedAt: z.string(),
            status: z.enum(['pending_ifc_review', 'approved', 'rejected']),
            ifcProgrammeItemId: z.string().nullable(),
            ifcDecisionNote: z.string().nullable(),
            ifcDecidedAt: z.string().nullable(),
        })
        .nullable(),
});
export type PcfDeviationDetail = z.infer<typeof pcfDeviationDetailSchema>;

// ---------------------------------------------------------------------------
// Dashboard — heat-map by IM
// ---------------------------------------------------------------------------

export const imDeviationCellSchema = z.object({
    imName: z.string(),
    totalDeviations: z.number().int(),
    deliberateCount: z.number().int(),
    driftCount: z.number().int(),
    criticalCount: z.number().int(),
});
export type ImDeviationCell = z.infer<typeof imDeviationCellSchema>;

export const pcfDashboardSchema = z.object({
    portfoliosBenchmarked: z.number().int(),
    portfoliosInBreach: z.number().int(),
    openDeviations: z.number().int(),
    deliberateCount: z.number().int(),
    driftCount: z.number().int(),
    heatMap: z.array(imDeviationCellSchema),
    models: z.array(houseModelSchema),
    deviations: z.array(pcfDeviationDetailSchema),
});
export type PcfDashboard = z.infer<typeof pcfDashboardSchema>;

// ---------------------------------------------------------------------------
// Buy-list change impact
// ---------------------------------------------------------------------------

export const buyListImpactLineSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    portfolioId: z.string(),
    currentWeightPct: z.number(),
    currentValueGbp: z.number(),
    impactNote: z.string(),
});
export type BuyListImpactLine = z.infer<typeof buyListImpactLineSchema>;

export const buyListChangeSchema = z.object({
    id: z.string(),
    changeRef: z.string(),
    kind: z.enum(['add', 'remove']),
    listKind: z.enum(['approved_buy', 'restricted']),
    scopeKind: z.enum(['model', 'mandate']),
    scopeId: z.string(),
    securityId: z.string(),
    securityName: z.string(),
    reason: z.string(),
    effectiveDate: z.string(),
    affectedClients: z.array(buyListImpactLineSchema),
    affectedClientCount: z.number().int(),
    totalAffectedValueGbp: z.number(),
    status: z.enum(['preview', 'committed']),
    createdAt: z.string(),
    committedAt: z.string().nullable(),
});
export type BuyListChange = z.infer<typeof buyListChangeSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const justifyDeviationRequestSchema = z.object({
    rationale: z.string().min(1, 'A rationale is required'),
    by: z.string(),
});
export type JustifyDeviationRequest = z.infer<typeof justifyDeviationRequestSchema>;

export const buyListChangeRequestSchema = z.object({
    kind: z.enum(['add', 'remove']),
    listKind: z.enum(['approved_buy', 'restricted']),
    scopeKind: z.enum(['model', 'mandate']),
    scopeId: z.string(),
    securityId: z.string(),
    securityName: z.string(),
    reason: z.string().min(1, 'A reason is required'),
    effectiveDate: z.string(),
    // No `by`: the Action's audit envelope records the authenticated actor.
});
export type BuyListChangeRequest = z.infer<typeof buyListChangeRequestSchema>;

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const pcfContract = c.router(
    {
        dashboard: {
            method: 'GET',
            path: '/pcf/dashboard',
            query: z.object({ imName: z.string().optional() }).optional(),
            responses: { 200: pcfDashboardSchema },
            summary: 'Framework compliance dashboard with per-IM deviation heat-map',
        },
        listModels: {
            method: 'GET',
            path: '/pcf/models',
            responses: { 200: z.object({ models: z.array(houseModelSchema) }) },
            summary: 'House model portfolio catalogue',
        },
        getModel: {
            method: 'GET',
            path: '/pcf/models/:modelId',
            responses: {
                200: z.object({ model: houseModelSchema }),
                404: errorBody,
            },
            summary: 'Single house model portfolio with allocation bands',
        },
        buyList: {
            method: 'GET',
            path: '/pcf/buy-list',
            responses: {
                200: z.object({ entries: z.array(securityListEntrySchema) }),
            },
            summary: 'Firm-approved buy list',
        },
        restrictedList: {
            method: 'GET',
            path: '/pcf/restricted-list',
            responses: {
                200: z.object({ entries: z.array(securityListEntrySchema) }),
            },
            summary: 'Restricted / prohibited instrument list',
        },
        getDeviation: {
            method: 'GET',
            path: '/pcf/deviations/:id',
            responses: {
                200: z.object({ deviation: pcfDeviationDetailSchema }),
                404: errorBody,
            },
            summary: 'Single PCF deviation detail with justification workflow state',
        },
        justifyDeviation: {
            method: 'POST',
            path: '/pcf/deviation/:id/justify',
            body: justifyDeviationRequestSchema,
            responses: {
                200: z.object({
                    ok: z.boolean(),
                    deviation: pcfDeviationDetailSchema,
                    ifcProgrammeItemId: z.string(),
                }),
                404: errorBody,
                409: errorBody,
            },
            summary: 'IM submits a deviation rationale; emits an IFC programme item',
        },
        buyListChange: {
            method: 'POST',
            path: '/pcf/buy-list/change',
            body: buyListChangeRequestSchema,
            responses: { 200: z.object({ change: buyListChangeSchema }) },
            summary: 'Compute the affected-client impact preview for a buy-list change',
        },
        commitBuyListChange: {
            method: 'POST',
            path: '/pcf/buy-list/change/:id/commit',
            // The Action's audit envelope records the authenticated actor; the
            // request no longer supplies a display name.
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.boolean(), change: buyListChangeSchema }),
                404: errorBody,
                409: errorBody,
            },
            summary: 'Commit a previewed buy-list change and re-snapshot portfolios',
        },
        getBuyListChange: {
            method: 'GET',
            path: '/pcf/buy-list/change/:id',
            responses: {
                200: z.object({ change: buyListChangeSchema }),
                404: errorBody,
            },
            summary: 'Retrieve a buy-list change impact preview / committed record',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
